// The deployment profile as the screens read it: the environment this deployment runs in and the
// ecosystems it serves. The shell reads it once (the query is cached for the session) and renders
// nothing that depends on it until it is read, so no screen presumes OFB, a transmitter or an
// environment; a screen reads it through the same gate and gets the cached profile at once.
import { useQuery } from '@tanstack/react-query';
import { Option, Result } from 'effect';
import { Ecosystem, ecosystemPolicy } from '@finnest/power-admin-contracts';
import type { PowerDeploymentProfile } from '@finnest/power-admin-contracts';
import { failureRefusal } from '@finnest/power-admin-client/deployment';
import { ErrorState, LoadingState, failedAtOf } from '../ui/states.tsx';
import { deploymentProfileQueryOptions } from './edge.ts';

/** The environment a deployment runs in, as the shell's badge names it. */
export const ENVIRONMENT_LABEL = {
  dev: 'Desenvolvimento',
  sandbox: 'Sandbox do regulador',
  prod: 'Produção',
} as const satisfies Record<PowerDeploymentProfile['environment'], string>;

/** The ecosystems a deployment serves, in the vocabulary's order; at least one. */
export type ServedEcosystems = readonly [Ecosystem, ...Ecosystem[]];

const PROFILE_SUBJECT = 'o perfil da implantação';

/** The ecosystems `profile` switches on. */
export const servedEcosystemsOf = (profile: PowerDeploymentProfile): readonly Ecosystem[] =>
  Ecosystem.literals.filter((ecosystem) => profile.ecosystems[ecosystem].status === 'enabled');

/** Renders `children` with the deployment profile once it is read. */
export function DeploymentGate({
  children,
}: {
  readonly children: (profile: PowerDeploymentProfile) => JSX.Element;
}): JSX.Element {
  const profileQuery = useQuery(deploymentProfileQueryOptions);
  if (profileQuery.status === 'pending') {
    return <LoadingState label={PROFILE_SUBJECT} layout="page" />;
  }
  // The query function answers with a Result, so a rejection is an unexpected defect; both are a
  // profile the screen does not have, and the operator can ask again.
  if (profileQuery.status === 'error' || Result.isFailure(profileQuery.data)) {
    return (
      <ErrorState
        onRetry={() => {
          void profileQuery.refetch();
        }}
        retrying={profileQuery.isFetching}
        subject={PROFILE_SUBJECT}
        failedAt={failedAtOf([profileQuery])}
        reason={Option.none()}
        refusal={Option.fromUndefinedOr(profileQuery.data).pipe(
          Option.flatMap(Result.getFailure),
          Option.flatMap(failureRefusal),
        )}
        traceable
      />
    );
  }
  return children(profileQuery.data.success);
}

/** Renders `children` with the ecosystems the deployment serves, or says it serves none. */
export function ServedEcosystemsGate({
  children,
}: {
  readonly children: (ecosystems: ServedEcosystems, profile: PowerDeploymentProfile) => JSX.Element;
}): JSX.Element {
  return (
    <DeploymentGate>
      {(profile) => {
        const [first, ...rest] = servedEcosystemsOf(profile);
        if (first === undefined) {
          return (
            <output>
              Esta implantação não atende nenhum ecossistema regulado (
              {Ecosystem.literals
                .map((ecosystem) => ecosystemPolicy(ecosystem).apiFamilyName)
                .join(', ')}{' '}
              desligados).
            </output>
          );
        }
        return children([first, ...rest], profile);
      }}
    </DeploymentGate>
  );
}
